import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import {
    AbstractControl,
    UntypedFormArray,
    UntypedFormBuilder,
    UntypedFormControl,
    UntypedFormGroup,
} from '@angular/forms';
import { MatIconRegistry } from '@angular/material/icon';
import { DomSanitizer } from '@angular/platform-browser';
import { EMPTY_ARRAY_LENGTH, FIRST_ELEMENT_INDEX, USER_INPUT_DEBOUNCE_TIME } from '@modeso/aumico-lib-core-fe';
import { GetAccountingBasisTranslationDto, GetLegalFormTranslationDto } from '@modeso/types__aumico-lib-settings-be';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { debounceTime } from 'rxjs/operators';
import { GetAnnualReportTemplateTranslation } from '../../dtos/get-annual-report-template-translation.dto';

@UntilDestroy()
@Component({
    selector: 'aumico-annual-report-edit-annual-report-templates-translations',
    templateUrl: './edit-annual-report-templates-translations.component.html',
    styleUrls: ['./edit-annual-report-templates-translations.component.scss'],
})
export class EditAnnualReportTemplatesTranslationsComponent implements OnInit {
    public formGroup: UntypedFormGroup | undefined;
    public translationsFormArray: UntypedFormArray | undefined;
    public displayedColumns: string[] = ['name', 'currentLocale', 'selectedLocale'];
    public isCompleted: boolean;

    private tableDataValues: GetAnnualReportTemplateTranslation[] = [];
    @Input() set tableData(value: GetAnnualReportTemplateTranslation[]) {
        if (value != null && this.selectedLocaleValue != null) {
            this.tableDataValues = value;
            this.buildForm();
        }
    }

    public selectedLocaleValue: string | undefined;
    @Input() set selectedLocale(value: string | undefined) {
        if (value != null && this.tableDataValues != null) {
            this.selectedLocaleValue = value;
            this.buildForm();
        }
    }

    @Input() currentLocale = 'de-CH';
    @Input() panelTitleKey: string;
    @Input() panelOpenState = false;

    @Output() translationValuesChanged: EventEmitter<GetAnnualReportTemplateTranslation[]> = new EventEmitter<
        GetAnnualReportTemplateTranslation[]
    >();

    constructor(
        private readonly formBuilder: UntypedFormBuilder,
        private matIconRegistry: MatIconRegistry,
        private domSanitizer: DomSanitizer,
    ) {
        this.addSvgIcon('aumico-pattern-icon-check-circle');
        this.addSvgIcon('aumico-pattern-icon-wrong-circle');
        this.buildForm();
    }

    ngOnInit(): void {}

    private addSvgIcon(name: string): void {
        this.matIconRegistry.addSvgIcon(
            name,
            this.domSanitizer.bypassSecurityTrustResourceUrl(`/assets/icons/${name}.svg`),
        );
    }

    private buildForm(): void {
        this.formGroup = this.formBuilder.group({
            translationsFormArray: this.formBuilder.array([]),
        });
        this.translationsFormArray = this.getAllAsFormArray();

        this.monitorFormChanges();

        this.formGroup.setControl('translationsFormArray', this.translationsFormArray);
        this.formGroup.updateValueAndValidity();
    }

    private getAllAsFormArray(): UntypedFormArray {
        const formGroups: AbstractControl[] = this.tableDataValues.map((entry) => this.getTableEntryFormGroup(entry));
        return new UntypedFormArray(formGroups);
    }

    private getTableEntryFormGroup(tableEntry: GetAnnualReportTemplateTranslation): UntypedFormGroup {
        const dataSourceFormGroup: { [key: string]: AbstractControl } = {};
        dataSourceFormGroup['name'] = new UntypedFormControl(tableEntry['name']);
        if ('caption' in tableEntry) {
            dataSourceFormGroup[this.currentLocale] = new UntypedFormControl(tableEntry.caption[this.currentLocale]);
            dataSourceFormGroup[this.selectedLocaleValue] = new UntypedFormControl(
                tableEntry.caption[this.selectedLocaleValue],
            );
        }
        return new UntypedFormGroup(dataSourceFormGroup);
    }

    private monitorFormChanges(): void {
        if (this.formGroup == null) {
            throw new Error('Form group is undefined.');
        }
        this.formGroup.valueChanges
            .pipe(debounceTime(USER_INPUT_DEBOUNCE_TIME), untilDestroyed(this))
            .subscribe((formValue) => {
                const translationResults = [];

                if (this.tableDataValues[FIRST_ELEMENT_INDEX] == undefined) {
                    return;
                }

                formValue.translationsFormArray.forEach((value) => {
                    if ('caption' in this.tableDataValues[FIRST_ELEMENT_INDEX]) {
                        translationResults.push({
                            name: value.name,
                            caption: {
                                [this.currentLocale]: value[this.currentLocale],
                                [this.selectedLocaleValue]: value[this.selectedLocaleValue],
                            },
                        });
                    }
                });

                this.translationValuesChanged.emit(translationResults);

                if (
                    this.tableDataValues.length > EMPTY_ARRAY_LENGTH &&
                    'caption' in this.tableDataValues[FIRST_ELEMENT_INDEX] &&
                    this.selectedLocaleValue != null
                ) {
                    this.checkIfTranslationsCaptionsAreCompleted(translationResults);
                } else if (this.tableDataValues.length === EMPTY_ARRAY_LENGTH) {
                    this.isCompleted = true;
                }
            });
    }

    private checkIfTranslationsCaptionsAreCompleted(
        translationResults: GetAccountingBasisTranslationDto[] | GetLegalFormTranslationDto[],
    ): void {
        this.isCompleted = true;
        translationResults.forEach((translation) => {
            if (
                translation['text'] != null &&
                translation['text'][this.selectedLocaleValue] != null &&
                translation['text'][this.selectedLocaleValue].length > EMPTY_ARRAY_LENGTH &&
                translation['text'][this.currentLocale] != null &&
                translation['text'][this.currentLocale].length > EMPTY_ARRAY_LENGTH
            ) {
                this.isCompleted = this.isCompleted && true;
            } else {
                this.isCompleted = false;
            }
        });
    }
}
